// Collapsible folder-structure picker for the Drive-sync surfaces, owning the
// recursion and expand/collapse state in one place.
//
// Lazy: the BE only sends the top couple of folder levels (a full recursive
// walk timed out behind the 29s API Gateway window on large folders). A node
// flagged ``has_unloaded_children`` loads its subfolders on demand via
// ``onLoadChildren`` when the operator clicks its chevron. Unchecking a
// folder still excludes its WHOLE subtree at sync time, so the operator never
// has to expand a folder just to exclude everything under it.
import { useState } from "react";

import { DriveFileLeaf, DriveFolderNode } from "../api/hooks/useDriveSync";
import { Chevron } from "./Chevron";

interface Props {
  tree: DriveFolderNode;
  excludedSubfolderIds: Set<string>;
  // The name rides along so a save can say what changed.
  onToggleExcluded: (folderId: string, name: string) => void;
  // Fetch one node (its next levels) when it's expanded. The picker caches the
  // loaded node so it loads once.
  onLoadChildren: (folderId: string) => Promise<DriveFolderNode>;
  // Given, each folder's files are listed with their own exclude checkbox.
  excludedFileIds?: Set<string>;
  onToggleExcludedFile?: (fileId: string, name: string) => void;
  // Excluded elsewhere and brought back only there: shown unticked, not editable.
  lockedFileIds?: Set<string>;
  // Given, a file under an excluded folder can be ticked to sync it on its own.
  trackedFileIds?: Set<string>;
  onToggleTrackedFile?: (fileId: string, name: string) => void;
}

const describeSize = (bytes: number | null): string => {
  if (bytes === null) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const FolderTree = ({
  tree,
  excludedSubfolderIds,
  onToggleExcluded,
  onLoadChildren,
  excludedFileIds,
  onToggleExcludedFile,
  lockedFileIds,
  trackedFileIds,
  onToggleTrackedFile,
}: Props) => {
  // Explicit expand choices keyed by folder id. Folders absent from the map
  // fall back to the include/exclude default rule below.
  const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});
  // Lazily-loaded children keyed by folder id (a node loads once on first
  // expand). Present here means "loaded", overriding the node's own empty
  // children from the depth-bounded server payload.
  const [loadedNodes, setLoadedNodes] = useState<Record<string, DriveFolderNode>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  // Folders whose files are listed; off by default, so folders do the include/exclude.
  const [showFilesIds, setShowFilesIds] = useState<Set<string>>(new Set());
  const listsFiles = !!onToggleExcludedFile;
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  const setExpanded = (folderId: string, next: boolean) => {
    setExpandOverrides((prev) => ({ ...prev, [folderId]: next }));
  };

  const loadChildren = async (folderId: string) => {
    setLoadingIds((prev) => new Set(prev).add(folderId));
    setErrorIds((prev) => {
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
    try {
      const loaded = await onLoadChildren(folderId);
      setLoadedNodes((prev) => ({ ...prev, [folderId]: loaded }));
      setExpanded(folderId, true);
    } catch {
      setErrorIds((prev) => new Set(prev).add(folderId));
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
    }
  };

  const renderNode = (
    node: DriveFolderNode,
    depth = 0,
    ancestorExcluded = false,
  ): JSX.Element => {
    const isRoot = depth === 0;
    const isSelfExcluded = excludedSubfolderIds.has(node.id);
    const effectivelyExcluded = isSelfExcluded || ancestorExcluded;
    const children = loadedNodes[node.id]?.children ?? node.children;
    // A node is "loaded" if the server already sent its children (not at
    // the depth boundary) or we've since fetched them lazily. Listing files, it also
    // needs its subfolders listed, so each shows its file count.
    const isLoaded =
      node.id in loadedNodes ||
      (!node.has_unloaded_children &&
        !(listsFiles && children.some((c) => c.has_unloaded_children)));
    const counted = listsFiles && (node.id in loadedNodes || !node.has_unloaded_children);
    const fileCount = loadedNodes[node.id]?.file_count ?? node.file_count;
    const allFiles = loadedNodes[node.id]?.files ?? node.files ?? [];
    const showFiles = showFilesIds.has(node.id);
    const files = listsFiles && showFiles ? allFiles : [];
    const filesOff = allFiles.filter(
      (f) => excludedFileIds?.has(f.id) || lockedFileIds?.has(f.id),
    ).length;
    const filesIn = allFiles.filter((f) => trackedFileIds?.has(f.id)).length;
    const hasAffordance =
      node.has_unloaded_children || children.length > 0 || files.length > 0;
    const isLoading = loadingIds.has(node.id);
    const hadError = errorIds.has(node.id);
    // Unloaded nodes render collapsed until the operator clicks to load
    // them, so a default-expanded boundary node can't trigger a fetch storm.
    const expanded =
      isLoaded && (node.id in expandOverrides ? expandOverrides[node.id] : !effectivelyExcluded);

    const onFileCount = () => {
      setShowFilesIds((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) next.delete(node.id);
        else next.add(node.id);
        return next;
      });
      if (!showFiles) {
        if (!isLoaded) {
          if (!isLoading) void loadChildren(node.id);
        } else setExpanded(node.id, true);
      }
    };

    const onChevron = () => {
      if (!isLoaded) {
        if (!isLoading) void loadChildren(node.id);
        return;
      }
      setExpanded(node.id, !expanded);
    };

    return (
      <div key={node.id}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingLeft: depth * 18,
            paddingTop: 2,
            paddingBottom: 2,
          }}
        >
          {hasAffordance ? (
            <button
              type="button"
              onClick={onChevron}
              aria-label={expanded ? "Collapse folder" : "Expand folder"}
              aria-expanded={expanded}
              disabled={isLoading}
              style={{
                width: 24,
                height: 24,
                flexShrink: 0,
                padding: 0,
                border: "none",
                background: "transparent",
                color: "#374151",
                fontSize: 16,
                lineHeight: 1,
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              {isLoading ? "…" : <Chevron tree open={expanded} />}
            </button>
          ) : (
            <span style={{ width: 24, flexShrink: 0, display: "inline-block" }} />
          )}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              cursor: isRoot || ancestorExcluded ? "default" : "pointer",
            }}
          >
            {isRoot ? (
              <span style={{ width: 16, display: "inline-block" }} />
            ) : (
              <input
                type="checkbox"
                checked={!effectivelyExcluded}
                onChange={() => onToggleExcluded(node.id, node.name)}
                disabled={ancestorExcluded}
              />
            )}
            <span
              style={{
                color: effectivelyExcluded ? "#aaa" : "#333",
                textDecoration: effectivelyExcluded ? "line-through" : "none",
              }}
            >
              {node.name}
            </span>
          </label>
          {counted && fileCount > 0 && (
            <button
              type="button"
              onClick={onFileCount}
              aria-pressed={showFiles}
              title={showFiles ? "Hide files" : "Show files to exclude them one by one"}
              style={{
                marginLeft: 8,
                padding: "0 6px",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                background: showFiles ? "#eef2ff" : "transparent",
                color: "#4b5563",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {fileCount} file{fileCount === 1 ? "" : "s"}
              {effectivelyExcluded
                ? filesIn > 0 && ` · ${filesIn} synced`
                : filesOff > 0 && ` · ${filesOff} excluded`}
            </button>
          )}
        </div>
        {hadError && (
          <div
            style={{
              paddingLeft: (depth + 1) * 18 + 16,
              fontSize: 12,
              color: "#b91c1c",
            }}
          >
            Couldn&apos;t load this folder.{" "}
            <button
              type="button"
              onClick={() => void loadChildren(node.id)}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                cursor: "pointer",
                padding: 0,
                fontSize: 12,
                textDecoration: "underline",
              }}
            >
              Retry
            </button>
          </div>
        )}
        {expanded &&
          children.map((child) =>
            renderNode(child, depth + 1, effectivelyExcluded),
          )}
        {expanded && files.map((file) => renderFile(file, depth + 1, effectivelyExcluded))}
      </div>
    );
  };

  const renderFile = (file: DriveFileLeaf, depth: number, folderExcluded: boolean) => {
    const locked = lockedFileIds?.has(file.id) ?? false;
    // Under an excluded folder a file is in only when tracked on its own.
    const trackable = folderExcluded && !locked && !!onToggleTrackedFile;
    const excluded = trackable
      ? !(trackedFileIds?.has(file.id) ?? false)
      : folderExcluded || locked || (excludedFileIds?.has(file.id) ?? false);
    const fixed = (folderExcluded && !trackable) || locked;
    const size = describeSize(file.size_bytes);
    return (
      <div
        key={file.id}
        style={{ display: "flex", alignItems: "center", paddingLeft: depth * 18 + 24 }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            cursor: fixed ? "default" : "pointer",
          }}
          title={locked ? "Excluded on the Files tab. Reinstate it there." : undefined}
        >
          <input
            type="checkbox"
            checked={!excluded}
            onChange={() =>
              trackable
                ? onToggleTrackedFile?.(file.id, file.name)
                : onToggleExcludedFile?.(file.id, file.name)
            }
            disabled={fixed}
          />
          <span
            style={{
              color: excluded ? "#aaa" : "#555",
              textDecoration: excluded ? "line-through" : "none",
            }}
          >
            {file.name}
            {size && <span style={{ color: "#888" }}> · {size}</span>}
          </span>
        </label>
      </div>
    );
  };

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 4,
        padding: 8,
        maxHeight: 240,
        overflowY: "auto",
        background: "#fafafa",
      }}
    >
      {renderNode(tree)}
    </div>
  );
};
